import { action, makeObservable, observable, flow } from "mobx";
import { getAgentInstruction, updateInstruction } from "../../services";

class InstructionsStore {
  isLoadingInstruction = false;
  isUpdatingInstruction = false;

  instructionErrorMessage = '';
  updateInstructionErrorMessage = '';

  currentInstruction = {
    instruction: ''
  };

  hasExistingInstruction = false;
  currentAgentUuid = null;
  instructionUuid = null;
  typingTimeout = null;
  autoSaveInterval = null;
  previousText = '';

  // Alert/Notification state
  showAlert = false;
  alertMessage = '';
  alertType = 'success'; // 'success', 'error', 'info', 'warning'

  // Reference to playgroundConfigStore for accessing agent name
  playgroundConfigStore = null;

  constructor() {
    makeObservable(this, {
      isLoadingInstruction: observable,
      isUpdatingInstruction: observable,
      
      instructionErrorMessage: observable,
      updateInstructionErrorMessage: observable,
      
      currentInstruction: observable,
      
      hasExistingInstruction: observable,
      currentAgentUuid: observable,
      instructionUuid: observable,
      typingTimeout: observable,
      autoSaveInterval: observable,
      previousText: observable,
      
      showAlert: observable,
      alertMessage: observable,
      alertType: observable,
      
      setCurrentInstruction: action,
      
      setInstructionError: action,
      
      showNotification: action,
      hideNotification: action,
      
      fetchAgentInstruction: flow,
      updateAgentInstruction: flow,
      
      startUpdateMode: action,
      stopAutoSave: action,
      handleUserTyping: action,
      updateInstructionOnChange: flow,
      
      reset: action,
      clearErrors: action
    });
  }

  setCurrentInstruction(data) { 
    this.currentInstruction = data; 
  }

  setHasExistingInstruction(hasInstruction) {
    this.hasExistingInstruction = hasInstruction;
  }

  setCurrentAgentUuid(agentUuid) {
    this.currentAgentUuid = agentUuid;
  }

  setPlaygroundConfigStore(playgroundConfigStore) {
    this.playgroundConfigStore = playgroundConfigStore;
  }
  

  startUpdateMode() {
    this.previousText = this.currentInstruction.instruction;
    
    this.autoSaveInterval = setInterval(() => {
      const currentText = this.currentInstruction.instruction;
      const hasChanged = currentText !== this.previousText;
      
      // Auto-save if there are changes (even if text is empty, to allow clearing instructions)
      if (hasChanged) {
        this.updateInstructionOnChange(currentText);
        this.previousText = currentText;
      }
    }, 5000);
  }

  handleUserTyping() {
    // Since we now always have an existing instruction, this method is no longer needed
    // All typing changes are handled by the auto-save in startUpdateMode
    return;
  }


  *updateInstructionOnChange(instructionText) {
    try {
      
      const data = { instruction_text: instructionText };
      const updateResponse = yield this.updateAgentInstruction(this.instructionUuid, data);
      
      //  Notification UPDATE (auto-save)
      if (updateResponse.status === 200){
          this.showNotification('Instruction successfully updated', 'success');
      }
    } catch (error) {
      this.showNotification('Error updating instruction', 'error');
    }
  }

  stopAutoSave() {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;
    }
    
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
  }

  setInstructionError(message) { 
    this.instructionErrorMessage = message;
  }

  showNotification(message, type = 'success') {
    this.alertMessage = message;
    this.alertType = type;
    this.showAlert = true;
  }

  hideNotification() {
    this.showAlert = false;
    this.alertMessage = '';
  }

  *fetchAgentInstruction(agentUuid) {
    try {
      this.isLoadingInstruction = true;
      this.instructionErrorMessage = '';
      
      this.setCurrentAgentUuid(agentUuid);
   
     
      const response = yield getAgentInstruction(agentUuid);
      const instructionData = {
        instruction: response.data?.body?.instruction || ''
      };
      this.setCurrentInstruction(instructionData);
      
      // Get instructionUuid from response (even if instruction is empty)
      this.instructionUuid = response.data?.data?.instructionUuid || response.data?.data?.instruction_uuid || null;
      
      // Always set as existing instruction if we have instructionUuid (even if instruction text is empty)
      // This allows auto-save to work for empty instructions that can be updated
      this.setHasExistingInstruction(!!this.instructionUuid);
      
      return instructionData;
    } catch (error) {
      if (error.response?.status === 404 && error.response?.data?.msgCode === 'agent_instruction_not_found') {
        const emptyInstructionData = {
          instruction: ''
        };
        this.setCurrentInstruction(emptyInstructionData);
        
        // Agent has no instruction at all (no instructionUuid)
        this.setHasExistingInstruction(false);
        this.instructionUuid = null;
        
        return emptyInstructionData;
      }
      
      this.setInstructionError("Error fetching agent instruction, try again later");
      throw error;
    } finally {
      this.isLoadingInstruction = false;
    }
  }


  *updateAgentInstruction(instructionUuid, instructionData) {
    try {
      this.isUpdatingInstruction = true;
      this.updateInstructionErrorMessage = '';
      
      const response = yield updateInstruction(instructionUuid, instructionData);
      return response;
    } catch (error) {
      this.setInstructionError("Error updating instruction, try again later");
      throw error;
    } finally {
      this.isUpdatingInstruction = false;
    }
  }


  clearErrors() {
    this.instructionErrorMessage = '';
    this.updateInstructionErrorMessage = '';
  }

  reset() {
    this.stopAutoSave();
    
    this.isLoadingInstruction = false;
    this.isUpdatingInstruction = false;
    
    this.clearErrors();
    
    this.currentInstruction = {
      instruction: ''
    };
    
    this.stopAutoSave();
    this.previousText = '';
    this.hasExistingInstruction = false;
    this.currentAgentUuid = null;
    this.instructionUuid = null;
    this.typingTimeout = null;
    
    // Reset notification state
    this.showAlert = false;
    this.alertMessage = '';
    this.alertType = 'success';
  }
}

export default InstructionsStore;
